import type { MouseEvent } from 'react';
import { ArrowDown } from 'lucide-react';
import ContactSection from '@/components/ContactSection';
import DirectAnswerBlock from '@/components/knowledge/DirectAnswerBlock';
import { ProposalProcessBlock } from '@/components/knowledge/ProposalProcessBlocks';
import SEOHead from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { getAiAnswerEntryById } from '@/lib/aiAnswersCatalog';
import { CONTACT_FORM_FIRST_FIELD_ID, CONTACT_FORM_ID } from '@/lib/contactDetails';
import { SITE_URL } from '@/lib/seo';
import { buildBreadcrumbSchema, buildFaqSchema } from '@/lib/schema';

const jumpToForm = (event: MouseEvent<HTMLAnchorElement>) => {
  const form = document.getElementById(CONTACT_FORM_ID);
  if (!form) return;
  event.preventDefault();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  form.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  document.getElementById(CONTACT_FORM_FIRST_FIELD_ID)?.focus({ preventScroll: true });
};

const ContactPage = () => {
  const { lang } = useLanguage();
  const contactDirectAnswer = getAiAnswerEntryById('contact-figtures', lang);
  const breadcrumbSchema = buildBreadcrumbSchema([
    { name: lang === 'tr' ? 'Ana Sayfa' : 'Home', item: SITE_URL },
    { name: lang === 'tr' ? 'İletişim' : 'Contact', item: `${SITE_URL}/contact` },
  ]);
  const contactFaqSchema =
    contactDirectAnswer &&
    buildFaqSchema(
      [
        {
          question: contactDirectAnswer.questionText,
          answer: contactDirectAnswer.directAnswerText,
        },
      ],
      {
        canonicalPath: '/contact',
        inLanguage: lang === 'tr' ? 'tr-TR' : 'en-US',
        locale: lang,
      },
    );

  return (
    <main className="pt-16 relative overflow-hidden">
      <SEOHead
        title={lang === 'tr' ? 'İletişim | Figtures' : 'Contact | Figtures'}
        description={
          lang === 'tr'
            ? 'Her sektörden AI, SaaS ve özel yazılım projeniz için Figtures ile görüşün. Türkiye’nin tüm şehirlerine ve yurt dışına hizmet; fiyat proje analiziyle belirlenir.'
            : 'Talk to Figtures about AI, SaaS, or custom software in any sector. We serve every city in Turkey and clients abroad; pricing follows a project analysis.'
        }
        canonical="/contact"
        jsonLd={[breadcrumbSchema, ...(contactFaqSchema ? [contactFaqSchema] : [])]}
      />
      {contactDirectAnswer ? (
        <div className="container mx-auto px-6 lg:px-12 pt-6 pb-2">
          <DirectAnswerBlock
            answer={contactDirectAnswer.directAnswerText}
            label={lang === 'tr' ? 'Nasıl ulaşılır?' : 'How to reach us'}
            className="max-w-3xl mx-auto border-border/50 bg-secondary/15 p-5 sm:p-6 [&_.geo-direct-answer]:text-base sm:[&_.geo-direct-answer]:text-lg"
          />
        </div>
      ) : null}
      {/* Mobile first screen: the fixed cookie banner covers everything below ~550px, so the form jump sits above the H1. */}
      <div className="container mx-auto px-6 lg:px-12 pt-3 pb-1">
        <div className="max-w-3xl mx-auto">
          <a
            href={`#${CONTACT_FORM_ID}`}
            onClick={jumpToForm}
            data-testid="contact-form-jump"
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-foreground px-8 py-3.5 font-body text-sm font-medium text-background hover:opacity-90 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {lang === 'tr' ? 'Ücretsiz keşif görüşmesi talep edin' : 'Book a free discovery call'}
            <ArrowDown size={16} aria-hidden="true" />
          </a>
        </div>
      </div>
      <ContactSection headingLevel="h1" />
      <div className="container mx-auto px-6 lg:px-12 pb-20">
        <ProposalProcessBlock lang={lang} variant="compact" className="max-w-5xl mx-auto" />
      </div>
    </main>
  );
};

export default ContactPage;
